import {Box} from "@chakra-ui/react";
import * as React from "react";
import {useCallback, useLayoutEffect, useMemo, useRef, useState} from "react";
import {FlowEdge as EdgeType, FlowNode as NodeType, FlowRuleContext, FlowRuleEngine} from "./types";
import {useElementSize} from "./useElementSize";
import FlowEdge from "./FlowEdge";
import FlowNode from "./FlowNode";

type Props = {
    nodes: NodeType[];
    edges: EdgeType[];
    layoutKey?: string;
    onSelectNode?: (node: NodeType, isCtrl: boolean) => void;
    onClearSelection?: () => void;
    showImplicitPaths?: boolean;
    ruleEngine?: FlowRuleEngine;
    selectedNodeId?: string;
};

type ViewState = {
    scale: number;
    tx: number;
    ty: number;
};

const MIN_SCALE = 0.3;
const MAX_SCALE = 3;
const ZOOM_IN_FACTOR = 1.08;
const ZOOM_OUT_FACTOR = 0.92;
const DRAG_THRESHOLD_PX = 3;
// Higher count improves hit testing accuracy at the cost of more samples per edge.
const EDGE_SAMPLE_COUNT = 18;
// Max distance from a curve before we treat the cursor as "not over any edge".
const EDGE_HIT_THRESHOLD_PX = 60;
const INITIAL_FIT_PADDING = 32;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const FlowCanvas = ({
    nodes,
    edges,
    layoutKey,
    onSelectNode,
    onClearSelection,
    showImplicitPaths,
    ruleEngine,
    selectedNodeId
}: Props) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const {width, height} = useElementSize(containerRef);
    const [view, setView] = useState<ViewState>({scale: 1, tx: 0, ty: 0});
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const dragStateRef = useRef<{x: number; y: number; tx: number; ty: number}>({x: 0, y: 0, tx: 0, ty: 0});
    const dragMovedRef = useRef<boolean>(false);
    const lastLayoutKeyRef = useRef<string>("");
    const prevBoundsRef = useRef<{minX: number; minY: number; maxX: number; maxY: number} | undefined>(undefined);
    const hasInitialFitRef = useRef<boolean>(false);

    const nodeMap = useMemo(() => {
        return new Map(nodes.map((node) => [node.id, node]));
    }, [nodes]);

    const edgePaths = useMemo(() => {
        const sampleBezier = (startX: number, startY: number, cp1x: number, cp1y: number, cp2x: number, cp2y: number, endX: number, endY: number) => {
            // Sample the cubic Bezier so we can estimate cursor-to-curve distance quickly.
            const points: {x: number; y: number}[] = [];
            for (let i = 0; i <= EDGE_SAMPLE_COUNT; i += 1) {
                const t = i / EDGE_SAMPLE_COUNT;
                const mt = 1 - t;
                const x = mt * mt * mt * startX
                    + 3 * mt * mt * t * cp1x
                    + 3 * mt * t * t * cp2x
                    + t * t * t * endX;
                const y = mt * mt * mt * startY
                    + 3 * mt * mt * t * cp1y
                    + 3 * mt * t * t * cp2y
                    + t * t * t * endY;
                points.push({x, y});
            }
            return points;
        };

        return edges.map((edge) => {
            const source = nodeMap.get(edge.fromId);
            const target = nodeMap.get(edge.toId);
            if (!source || !target) {
                return undefined;
            }
            const startX = source.x + source.width;
            const startY = source.y + source.height / 2;
            const endX = target.x;
            const endY = target.y + target.height / 2;
            const midX = (startX + endX) / 2;
            const samples = sampleBezier(startX, startY, midX, startY, midX, endY, endX, endY);
            return {
                id: edge.id,
                edge,
                startX,
                startY,
                midX,
                endX,
                endY,
                samples
            };
        }).filter((value): value is NonNullable<typeof value> => !!value);
    }, [edges, nodeMap]);

    const bounds = useMemo(() => {
        if (nodes.length === 0) {
            return undefined;
        }
        let minX = Number.POSITIVE_INFINITY;
        let minY = Number.POSITIVE_INFINITY;
        let maxX = Number.NEGATIVE_INFINITY;
        let maxY = Number.NEGATIVE_INFINITY;
        nodes.forEach((node) => {
            minX = Math.min(minX, node.x);
            minY = Math.min(minY, node.y);
            maxX = Math.max(maxX, node.x + node.width);
            maxY = Math.max(maxY, node.y + node.height);
        });
        return {minX, minY, maxX, maxY};
    }, [nodes]);


    useLayoutEffect(() => {
        if (hasInitialFitRef.current || !bounds || width <= 0 || height <= 0) {
            return;
        }
        const graphWidth = bounds.maxX - bounds.minX;
        const graphHeight = bounds.maxY - bounds.minY;
        if (graphWidth <= 0 || graphHeight <= 0) {
            return;
        }
        const availableWidth = Math.max(0, width - INITIAL_FIT_PADDING * 2);
        const availableHeight = Math.max(0, height - INITIAL_FIT_PADDING * 2);
        const scale = clamp(Math.min(availableWidth / graphWidth, availableHeight / graphHeight), MIN_SCALE, MAX_SCALE);
        const centerX = bounds.minX + graphWidth / 2;
        const centerY = bounds.minY + graphHeight / 2;
        setView({scale, tx: width / 2 - centerX * scale, ty: height / 2 - centerY * scale});
        hasInitialFitRef.current = true;
        prevBoundsRef.current = bounds;
    }, [bounds, height, width]);

    useLayoutEffect(() => {
        // Keep the same relative center in view when layout spacing changes.
        if (!layoutKey || !bounds || width <= 0 || height <= 0) {
            prevBoundsRef.current = bounds;
            return;
        }
        if (layoutKey !== lastLayoutKeyRef.current && prevBoundsRef.current) {
            const prevBounds = prevBoundsRef.current;
            const prevWidth = prevBounds.maxX - prevBounds.minX;
            const prevHeight = prevBounds.maxY - prevBounds.minY;
            const nextWidth = bounds.maxX - bounds.minX;
            const nextHeight = bounds.maxY - bounds.minY;
            if (prevWidth > 0 && prevHeight > 0 && nextWidth > 0 && nextHeight > 0) {
                const centerWorldX = (width / 2 - view.tx) / view.scale;
                const centerWorldY = (height / 2 - view.ty) / view.scale;
                const ratioX = Math.min(1, Math.max(0, (centerWorldX - prevBounds.minX) / prevWidth));
                const ratioY = Math.min(1, Math.max(0, (centerWorldY - prevBounds.minY) / prevHeight));
                const nextCenterX = bounds.minX + ratioX * nextWidth;
                const nextCenterY = bounds.minY + ratioY * nextHeight;
                setView((prev) => ({
                    ...prev,
                    tx: width / 2 - nextCenterX * prev.scale,
                    ty: height / 2 - nextCenterY * prev.scale
                }));
            }
        }
        lastLayoutKeyRef.current = layoutKey;
        prevBoundsRef.current = bounds;
    }, [bounds, height, layoutKey, view.scale, view.tx, view.ty, width]);

    const handleWheel = useCallback((event: WheelEvent) => {
        event.preventDefault();
        event.stopPropagation();
        if (!containerRef.current) {
            return;
        }
        const rect = containerRef.current.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;
        const zoomFactor = event.deltaY < 0 ? ZOOM_IN_FACTOR : ZOOM_OUT_FACTOR;

        setView((prev) => {
            const nextScale = clamp(prev.scale * zoomFactor, MIN_SCALE, MAX_SCALE);
            const worldX = (mouseX - prev.tx) / prev.scale;
            const worldY = (mouseY - prev.ty) / prev.scale;
            const nextTx = mouseX - worldX * nextScale;
            const nextTy = mouseY - worldY * nextScale;
            return {scale: nextScale, tx: nextTx, ty: nextTy};
        });
    }, []);

    useLayoutEffect(() => {
        const node = containerRef.current;
        if (!node) {
            return undefined;
        }
        node.addEventListener("wheel", handleWheel, {passive: false});
        return () => {
            node.removeEventListener("wheel", handleWheel);
        };
    }, [handleWheel]);

    const findNearestEdge = useCallback((clientX: number, clientY: number) => {
        if (!containerRef.current || edgePaths.length === 0) {
            return undefined;
        }
        const rect = containerRef.current.getBoundingClientRect();
        const svgX = (clientX - rect.left - view.tx) / view.scale;
        const svgY = (clientY - rect.top - view.ty) / view.scale;
        // Distance from a point to a line segment.
        const pointToSegmentDistance = (px: number, py: number, ax: number, ay: number, bx: number, by: number) => {
            const dx = bx - ax;
            const dy = by - ay;
            if (dx === 0 && dy === 0) {
                const distX = px - ax;
                const distY = py - ay;
                return Math.sqrt(distX * distX + distY * distY);
            }
            const t = ((px - ax) * dx + (py - ay) * dy) / (dx * dx + dy * dy);
            const clamped = Math.max(0, Math.min(1, t));
            const projX = ax + clamped * dx;
            const projY = ay + clamped * dy;
            const distX = px - projX;
            const distY = py - projY;
            return Math.sqrt(distX * distX + distY * distY);
        };

        let bestId: string | undefined;
        let bestDistance = Number.POSITIVE_INFINITY;
        edgePaths.forEach((edgePath) => {
            const samples = edgePath.samples;
            for (let i = 0; i < samples.length - 1; i += 1) {
                const a = samples[i];
                const b = samples[i + 1];
                const distance = pointToSegmentDistance(svgX, svgY, a.x, a.y, b.x, b.y);
                if (distance < bestDistance) {
                    bestDistance = distance;
                    bestId = edgePath.id;
                }
            }
        });
        if (bestDistance > EDGE_HIT_THRESHOLD_PX) {
            return undefined;
        }
        return bestId;
    }, [edgePaths, view.scale, view.tx, view.ty]);

    const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.button !== 0) {
            return;
        }
        setIsDragging(true);
        dragMovedRef.current = false;
        dragStateRef.current = {
            x: event.clientX,
            y: event.clientY,
            tx: view.tx,
            ty: view.ty
        };
    };

    const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
        if (!isDragging) {
            return;
        }
        const dx = event.clientX - dragStateRef.current.x;
        const dy = event.clientY - dragStateRef.current.y;
        if (!dragMovedRef.current && (Math.abs(dx) > DRAG_THRESHOLD_PX || Math.abs(dy) > DRAG_THRESHOLD_PX)) {
            dragMovedRef.current = true;
        }
        setView((prev) => ({
            ...prev,
            tx: dragStateRef.current.tx + dx,
            ty: dragStateRef.current.ty + dy
        }));
    };

    const [hoveredEdgeId, setHoveredEdgeId] = useState<string | undefined>(undefined);
    const [pinnedEdgeId, setPinnedEdgeId] = useState<string | undefined>(undefined);
    const [hoveredNodeId, setHoveredNodeId] = useState<string | undefined>(undefined);
    const [pinnedNodeId, setPinnedNodeId] = useState<string | undefined>(undefined);
    const [walkTrailNodes, setWalkTrailNodes] = useState<string[]>([]);
    const [walkTrailEdges, setWalkTrailEdges] = useState<string[]>([]);
    const [walkCurrentNodeId, setWalkCurrentNodeId] = useState<string | undefined>(undefined);
    const walkMode = walkCurrentNodeId !== undefined;
    const trailNodeIds = useMemo(() => new Set(walkTrailNodes), [walkTrailNodes]);
    const trailEdgeIds = useMemo(() => new Set(walkTrailEdges), [walkTrailEdges]);
    const defaultStandNodeId = useMemo(() => {
        return nodes.find((node) => node.kind === "start")?.id ?? nodes[0]?.id;
    }, [nodes]);
    const standNodeId = walkCurrentNodeId ?? defaultStandNodeId;
    const pathNodes = useMemo(() => {
        const entries: string[] = [...walkTrailNodes];
        if (walkCurrentNodeId && entries[entries.length - 1] !== walkCurrentNodeId) {
            entries.push(walkCurrentNodeId);
        }
        if (entries.length > 0) {
            return entries;
        }
        return standNodeId ? [standNodeId] : [];
    }, [standNodeId, walkCurrentNodeId, walkTrailNodes]);
    const pathSet = useMemo(() => new Set(pathNodes), [pathNodes]);

    useLayoutEffect(() => {
        if (!selectedNodeId) {
            return;
        }
        if (selectedNodeId !== pinnedNodeId) {
            setPinnedNodeId(selectedNodeId);
            setPinnedEdgeId(undefined);
        }
        if (walkCurrentNodeId && selectedNodeId !== walkCurrentNodeId) {
            setWalkTrailNodes([]);
            setWalkTrailEdges([]);
            setWalkCurrentNodeId(undefined);
        }
    }, [pinnedNodeId, selectedNodeId, walkCurrentNodeId]);
    const ruleContext: FlowRuleContext = useMemo(() => ({
        pathNodes,
        pathSet,
        nodes,
        edges
    }), [edges, nodes, pathNodes, pathSet]);
    const availabilityByNodeId = useMemo(() => {
        const availability = new Map<string, boolean>();
        if (!ruleEngine) {
            return availability;
        }
        nodes.forEach((node) => {
            if (node.kind === "start") {
                availability.set(node.id, true);
                return;
            }
            availability.set(node.id, ruleEngine.canReachNode(node, ruleContext));
        });
        return availability;
    }, [nodes, ruleContext, ruleEngine]);

    const explicitEdgeKeys = useMemo(() => new Set(edges.map((edge) => `${edge.fromId}->${edge.toId}`)), [edges]);
    const logPathEvaluation = useCallback((label: string) => {
        if (!ruleEngine) {
            return;
        }
        if (!standNodeId) {
            return;
        }
        const sourceId = standNodeId;
        const rows = nodes
            .filter((node) => node.kind !== "start")
            .map((node) => {
                const reachable = ruleEngine.canReachNode(node, ruleContext);
                const hasExplicitEdge = explicitEdgeKeys.has(`${sourceId}->${node.id}`);
                return {
                    id: node.id,
                    label: node.label,
                    reachable,
                    implicitlyReachable: reachable && node.id !== sourceId && !hasExplicitEdge
                };
            });
        // eslint-disable-next-line no-console
        console.log(`[FlowGraph] ${label}`, {
            sourceId,
            pathNodes: ruleContext.pathNodes,
            pathSet: Array.from(ruleContext.pathSet),
            nodes: rows
        });
    }, [explicitEdgeKeys, nodes, ruleContext, ruleEngine, standNodeId]);
    const buildImplicitEdgesForContext = useCallback((sourceId: string | undefined, context: FlowRuleContext) => {
        if (!ruleEngine) {
            return [];
        }
        if (!sourceId) {
            return [];
        }
        const items: EdgeType[] = [];
        nodes.forEach((target) => {
            if (target.kind === "start" || target.id === sourceId) {
                return;
            }
            if (!ruleEngine.canReachNode(target, context)) {
                return;
            }
            const edgeKey = `${sourceId}->${target.id}`;
            if (explicitEdgeKeys.has(edgeKey)) {
                return;
            }
            items.push({
                id: `implicit-${sourceId}-${target.id}`,
                fromId: sourceId,
                toId: target.id,
                label: "",
                kind: "implicit"
            });
        });
        return items;
    }, [explicitEdgeKeys, nodes, ruleEngine]);
    const implicitStandEdges = useMemo(() => {
        if (!showImplicitPaths || !ruleEngine) {
            return [];
        }
        return buildImplicitEdgesForContext(standNodeId, ruleContext);
    }, [buildImplicitEdgesForContext, ruleContext, ruleEngine, showImplicitPaths, standNodeId]);
    const implicitSelectedEdges = useMemo(() => {
        if (!showImplicitPaths || !ruleEngine) {
            return [];
        }
        const selectedId = pinnedNodeId ?? selectedNodeId;
        if (!selectedId || selectedId === standNodeId) {
            return [];
        }
        if (walkMode) {
            return buildImplicitEdgesForContext(selectedId, ruleContext);
        }
        const context: FlowRuleContext = {
            pathNodes: [selectedId],
            pathSet: new Set([selectedId]),
            nodes,
            edges
        };
        return buildImplicitEdgesForContext(selectedId, context);
    }, [buildImplicitEdgesForContext, edges, pinnedNodeId, ruleContext, ruleEngine, selectedNodeId, showImplicitPaths, standNodeId, walkMode]);
    const implicitEdges = useMemo(() => {
        if (implicitSelectedEdges.length === 0) {
            return implicitStandEdges;
        }
        const map = new Map<string, EdgeType>();
        implicitStandEdges.forEach((edge) => map.set(edge.id, edge));
        implicitSelectedEdges.forEach((edge) => map.set(edge.id, edge));
        return Array.from(map.values());
    }, [implicitSelectedEdges, implicitStandEdges]);
    const selectedFocusId = pinnedNodeId ?? selectedNodeId;
    const implicitHighlightEdges = useMemo(() => {
        if (!selectedFocusId) {
            return [];
        }
        return implicitEdges.filter((edge) => edge.fromId === selectedFocusId);
    }, [implicitEdges, selectedFocusId]);
    const implicitHighlightIds = useMemo(() => {
        return new Set(implicitHighlightEdges.map((edge) => edge.id));
    }, [implicitHighlightEdges]);
    const baseImplicitEdges = useMemo(() => {
        if (implicitHighlightIds.size === 0) {
            return implicitEdges;
        }
        return implicitEdges.filter((edge) => !implicitHighlightIds.has(edge.id));
    }, [implicitEdges, implicitHighlightIds]);

    const outgoingMap = useMemo(() => {
        const map = new Map<string, EdgeType[]>();
        edges.forEach((edge) => {
            if (!map.has(edge.fromId)) {
                map.set(edge.fromId, []);
            }
            map.get(edge.fromId)?.push(edge);
        });
        return map;
    }, [edges]);

    const findNearestNode = useCallback((clientX: number, clientY: number) => {
        if (!containerRef.current || nodes.length === 0) {
            return undefined;
        }
        const rect = containerRef.current.getBoundingClientRect();
        const svgX = (clientX - rect.left - view.tx) / view.scale;
        const svgY = (clientY - rect.top - view.ty) / view.scale;
        for (let i = nodes.length - 1; i >= 0; i -= 1) {
            const node = nodes[i];
            if (svgX >= node.x && svgX <= node.x + node.width && svgY >= node.y && svgY <= node.y + node.height) {
                return node.id;
            }
        }
        return undefined;
    }, [nodes, view.scale, view.tx, view.ty]);

    const isEdgeBlocked = useCallback((edge: EdgeType) => {
        if (!walkMode) {
            return false;
        }
        return availabilityByNodeId.get(edge.toId) === false;
    }, [availabilityByNodeId, walkMode]);

    const isReachableNode = useCallback((nodeId: string) => {
        return availabilityByNodeId.get(nodeId) === true;
    }, [availabilityByNodeId]);

    const handleHoverMove = (event: React.MouseEvent<HTMLDivElement>) => {
        if (isDragging) {
            return;
        }
        const nodeHit = findNearestNode(event.clientX, event.clientY);
        if (event.ctrlKey) {
            const current = standNodeId;
            if (current && nodeHit) {
                if (isReachableNode(nodeHit)) {
                    const candidates = outgoingMap.get(current) || [];
                    const match = candidates.find((edge) => edge.toId === nodeHit && !isEdgeBlocked(edge));
                    setHoveredNodeId(nodeHit);
                    setHoveredEdgeId(match?.id);
                    return;
                }
                const candidates = outgoingMap.get(current) || [];
                const match = candidates.find((edge) => edge.toId === nodeHit && !isEdgeBlocked(edge));
                if (match) {
                    setHoveredNodeId(nodeHit);
                    setHoveredEdgeId(match.id);
                    return;
                }
            }
            setHoveredNodeId(undefined);
            setHoveredEdgeId(undefined);
            return;
        }
        if (nodeHit) {
            setHoveredNodeId(nodeHit);
            setHoveredEdgeId(undefined);
            return;
        }
        setHoveredNodeId(undefined);
        const next = findNearestEdge(event.clientX, event.clientY);
        setHoveredEdgeId(next);
    };

    const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (isDragging) {
            return;
        }
        if (dragMovedRef.current) {
            dragMovedRef.current = false;
            return;
        }
        if (!event.ctrlKey && (walkCurrentNodeId || walkTrailNodes.length > 0 || walkTrailEdges.length > 0)) {
            setWalkTrailNodes([]);
            setWalkTrailEdges([]);
            setWalkCurrentNodeId(undefined);
        }
        const nodeHit = findNearestNode(event.clientX, event.clientY);
        const selectNode = (nodeId: string) => {
            const node = nodeMap.get(nodeId);
            if (node) {
                onSelectNode?.(node, event.ctrlKey);
            }
        };
        if (event.ctrlKey) {
            if (!nodeHit) {
                onClearSelection?.();
                return;
            }
            selectNode(nodeHit);
            if (walkCurrentNodeId) {
                logPathEvaluation(`Path select ${nodeHit}`);
            }
            if (nodeHit === walkCurrentNodeId) {
                if (walkTrailNodes.length > 1) {
                    const nextNodes = walkTrailNodes.slice(0, -1);
                    const nextEdges = walkTrailEdges.slice(0, -1);
                    setWalkTrailNodes(nextNodes);
                    setWalkTrailEdges(nextEdges);
                    setWalkCurrentNodeId(nextNodes[nextNodes.length - 1]);
                } else {
                    setWalkTrailNodes([]);
                    setWalkTrailEdges([]);
                    setWalkCurrentNodeId(undefined);
                }
                setPinnedNodeId(undefined);
                setPinnedEdgeId(undefined);
                setHoveredEdgeId(undefined);
                setHoveredNodeId(undefined);
                return;
            }
            if (walkTrailNodes.length > 0) {
                const trailIndex = walkTrailNodes.indexOf(nodeHit);
                if (trailIndex !== -1) {
                    const nextNodes = walkTrailNodes.slice(0, trailIndex + 1);
                    const nextEdges = walkTrailEdges.slice(0, trailIndex);
                    setWalkTrailNodes(nextNodes);
                    setWalkTrailEdges(nextEdges);
                    setWalkCurrentNodeId(nodeHit);
                    setPinnedNodeId(undefined);
                    setPinnedEdgeId(undefined);
                    setHoveredEdgeId(undefined);
                    setHoveredNodeId(undefined);
                    return;
                }
            }
            const nodeHitData = nodeMap.get(nodeHit);
            if (nodeHitData?.kind === "start") {
                setWalkTrailNodes([nodeHit]);
                setWalkTrailEdges([]);
                setWalkCurrentNodeId(nodeHit);
                logPathEvaluation(`Path start ${nodeHit}`);
                setPinnedNodeId(undefined);
                setPinnedEdgeId(undefined);
                setHoveredEdgeId(undefined);
                setHoveredNodeId(undefined);
                return;
            }
            const current = standNodeId;
            if (isReachableNode(nodeHit)) {
                if (!current) {
                    setWalkTrailNodes([nodeHit]);
                    setWalkTrailEdges([]);
                    setWalkCurrentNodeId(nodeHit);
                    logPathEvaluation(`Path start ${nodeHit}`);
                } else {
                    setWalkTrailNodes((prev) => {
                        const next = prev.length === 0 ? [current] : [...prev];
                        if (next[next.length - 1] !== current) {
                            next.push(current);
                        }
                        next.push(nodeHit);
                        return next;
                    });
                    const candidates = outgoingMap.get(current) || [];
                    const match = candidates.find((edge) => edge.toId === nodeHit && !isEdgeBlocked(edge));
                    if (match) {
                        setWalkTrailEdges((prev) => [...prev, match.id]);
                    }
                    setWalkCurrentNodeId(nodeHit);
                    logPathEvaluation(`Path step ${current} -> ${nodeHit}`);
                }
                setPinnedNodeId(undefined);
                setPinnedEdgeId(undefined);
                setHoveredEdgeId(undefined);
                setHoveredNodeId(undefined);
                return;
            }
            if (!current) {
                setWalkTrailNodes([nodeHit]);
                setWalkTrailEdges([]);
                setWalkCurrentNodeId(nodeHit);
                setPinnedNodeId(undefined);
                setPinnedEdgeId(undefined);
                setHoveredEdgeId(undefined);
                setHoveredNodeId(undefined);
                return;
            }
            const candidates = outgoingMap.get(current) || [];
            const match = candidates.find((edge) => edge.toId === nodeHit);
            if (!match) {
                return;
            }
            if (isEdgeBlocked(match)) {
                return;
            }
            setWalkTrailNodes((prev) => {
                const next = prev.length === 0 ? [current] : [...prev];
                if (next[next.length - 1] !== current) {
                    next.push(current);
                }
                next.push(nodeHit);
                return next;
            });
            setWalkTrailEdges((prev) => [...prev, match.id]);
            setWalkCurrentNodeId(nodeHit);
            setPinnedNodeId(undefined);
            setPinnedEdgeId(undefined);
            setHoveredEdgeId(undefined);
            setHoveredNodeId(undefined);
            return;
        }
        if (nodeHit) {
            selectNode(nodeHit);
            setPinnedNodeId((prev) => (prev === nodeHit ? undefined : nodeHit));
            setPinnedEdgeId(undefined);
            setHoveredEdgeId(undefined);
            return;
        }
        const next = findNearestEdge(event.clientX, event.clientY);
        if (!next) {
            setPinnedEdgeId(undefined);
            setHoveredEdgeId(undefined);
            setPinnedNodeId(undefined);
            setWalkTrailNodes([]);
            setWalkTrailEdges([]);
            setWalkCurrentNodeId(undefined);
            onClearSelection?.();
            return;
        }
        onClearSelection?.();
        setPinnedEdgeId((prev) => (prev === next ? undefined : next));
        setHoveredEdgeId(undefined);
    };

    const clearHover = () => {
        setHoveredEdgeId(undefined);
        setHoveredNodeId(undefined);
    };

    const stopDragging = () => {
        setIsDragging(false);
    };

    const activeNodeIds = useMemo(() => {
        const ids = new Set<string>();
        if (pinnedNodeId) {
            ids.add(pinnedNodeId);
        }
        if (hoveredNodeId) {
            ids.add(hoveredNodeId);
        }
        if (walkCurrentNodeId) {
            ids.add(walkCurrentNodeId);
        }
        return ids;
    }, [hoveredNodeId, pinnedNodeId, walkCurrentNodeId]);

    const highlightEdgeIds = useMemo(() => {
        const ids = new Set<string>();
        if (pinnedEdgeId) {
            ids.add(pinnedEdgeId);
        }
        if (hoveredEdgeId) {
            ids.add(hoveredEdgeId);
        }
        trailEdgeIds.forEach((id) => ids.add(id));
        implicitHighlightIds.forEach((id) => ids.add(id));
        if (activeNodeIds.size > 0) {
            edges.forEach((edge) => {
                const match = walkMode
                    ? activeNodeIds.has(edge.fromId)
                    : activeNodeIds.has(edge.fromId) || activeNodeIds.has(edge.toId);
                if (match) {
                    ids.add(edge.id);
                }
            });
        }
        return ids;
    }, [activeNodeIds, edges, hoveredEdgeId, pinnedEdgeId, trailEdgeIds, walkMode]);

    const highlightNodeIds = useMemo(() => {
        const ids = new Set<string>();
        if (highlightEdgeIds.size === 0) {
            activeNodeIds.forEach((id) => ids.add(id));
            trailNodeIds.forEach((id) => ids.add(id));
            return ids;
        }
        edges.forEach((edge) => {
            if (!highlightEdgeIds.has(edge.id)) {
                return;
            }
            ids.add(edge.fromId);
            ids.add(edge.toId);
        });
        implicitHighlightEdges.forEach((edge) => {
            ids.add(edge.fromId);
            ids.add(edge.toId);
        });
        activeNodeIds.forEach((id) => ids.add(id));
        trailNodeIds.forEach((id) => ids.add(id));
        return ids;
    }, [activeNodeIds, edges, highlightEdgeIds, implicitHighlightEdges, trailNodeIds]);

    const connectedNodeIds = useMemo(() => {
        const ids = new Set<string>();
        if (activeNodeIds.size === 0) {
            return ids;
        }
        edges.forEach((edge) => {
            if (activeNodeIds.has(edge.fromId)) {
                ids.add(edge.toId);
            }
            if (!walkMode && activeNodeIds.has(edge.toId)) {
                ids.add(edge.fromId);
            }
        });
        if (selectedFocusId) {
            implicitHighlightEdges.forEach((edge) => {
                if (edge.fromId === selectedFocusId) {
                    ids.add(edge.toId);
                }
                if (!walkMode && edge.toId === selectedFocusId) {
                    ids.add(edge.fromId);
                }
            });
        }
        activeNodeIds.forEach((id) => ids.delete(id));
        trailNodeIds.forEach((id) => ids.delete(id));
        return ids;
    }, [activeNodeIds, edges, implicitHighlightEdges, selectedFocusId, trailNodeIds, walkMode]);

    const baseEdges = useMemo(() => edges.filter((edge) => !highlightEdgeIds.has(edge.id)), [edges, highlightEdgeIds]);
    const focusedEdges = useMemo(() => edges.filter((edge) => highlightEdgeIds.has(edge.id)), [edges, highlightEdgeIds]);
    const graphImplicitEdges = useMemo(() => baseEdges.filter((edge) => edge.kind === "implicit"), [baseEdges]);
    const graphExplicitEdges = useMemo(() => baseEdges.filter((edge) => edge.kind !== "implicit"), [baseEdges]);
    const baseNodes = useMemo(() => nodes.filter((node) => !highlightNodeIds.has(node.id)), [nodes, highlightNodeIds]);
    const connectedNodes = useMemo(() => nodes.filter((node) => connectedNodeIds.has(node.id)), [nodes, connectedNodeIds]);
    const primaryNodes = useMemo(() => nodes.filter((node) => highlightNodeIds.has(node.id) && !connectedNodeIds.has(node.id)), [nodes, highlightNodeIds, connectedNodeIds]);
    const hasHighlight = highlightEdgeIds.size > 0 || highlightNodeIds.size > 0;

    // Render order (back to front):
    // 1) Base edge paths
    // 2) Base implicit edges (from the graph)
    // 3) Ad-hoc implicit paths from the standing point and selected node
    // 4) Base node shapes
    // 5) Base edge labels
    // 6) Highlighted edge paths
    // 7) Highlighted node shapes
    // 8) Highlighted edge labels
    // 9) Node labels (always on top)
    return (
        <Box
            ref={containerRef}
            height={"100%"}
            width={"100%"}
            overflow={"hidden"}
            cursor={isDragging ? "grabbing" : "grab"}
            userSelect={"none"}
            overscrollBehavior={"contain"}
            onMouseDown={handleMouseDown}
            onMouseMove={(event) => {
                handleMouseMove(event);
                handleHoverMove(event);
            }}
            onMouseUp={stopDragging}
            onMouseLeave={() => {
                stopDragging();
                clearHover();
            }}
            onClick={handleClick}
        >
            <svg
                width={width || "100%"}
                height={height || "100%"}
                style={{display: "block", userSelect: "none"}}
            >
                <g transform={`translate(${view.tx}, ${view.ty}) scale(${view.scale})`}>
                    {graphImplicitEdges.map((edge) => {
                        const source = nodeMap.get(edge.fromId);
                        const target = nodeMap.get(edge.toId);
                        if (!source || !target) {
                            return null;
                        }
                        return (
                            <FlowEdge
                                key={`edge-path-${edge.id}`}
                                edge={edge}
                                source={source}
                                target={target}
                                isDimmed={hasHighlight}
                                isBlocked={isEdgeBlocked(edge)}
                                renderLabel={false}
                            />
                        );
                    })}
                    {baseImplicitEdges.map((edge) => {
                        const source = nodeMap.get(edge.fromId);
                        const target = nodeMap.get(edge.toId);
                        if (!source || !target) {
                            return null;
                        }
                        return (
                            <FlowEdge
                                key={`edge-implicit-stand-${edge.id}`}
                                edge={edge}
                                source={source}
                                target={target}
                                isDimmed={hasHighlight}
                                renderLabel={false}
                            />
                        );
                    })}
                    {graphExplicitEdges.map((edge) => {
                        const source = nodeMap.get(edge.fromId);
                        const target = nodeMap.get(edge.toId);
                        if (!source || !target) {
                            return null;
                        }
                        return (
                            <FlowEdge
                                key={`edge-path-${edge.id}`}
                                edge={edge}
                                source={source}
                                target={target}
                                isDimmed={hasHighlight}
                                isBlocked={isEdgeBlocked(edge)}
                                renderLabel={false}
                            />
                        );
                    })}
                    {baseNodes.map((node) => (
                        <FlowNode
                            key={`node-shape-${node.id}`}
                            node={node}
                            isDimmed={hasHighlight}
                            isStanding={node.id === walkCurrentNodeId}
                            renderLabel={false}
                        />
                    ))}
                    {graphImplicitEdges.map((edge) => {
                        const source = nodeMap.get(edge.fromId);
                        const target = nodeMap.get(edge.toId);
                        if (!source || !target) {
                            return null;
                        }
                        return (
                            <FlowEdge
                                key={`edge-label-${edge.id}`}
                                edge={edge}
                                source={source}
                                target={target}
                                isDimmed={hasHighlight}
                                isBlocked={isEdgeBlocked(edge)}
                                renderPath={false}
                            />
                        );
                    })}
                    {graphExplicitEdges.map((edge) => {
                        const source = nodeMap.get(edge.fromId);
                        const target = nodeMap.get(edge.toId);
                        if (!source || !target) {
                            return null;
                        }
                        return (
                            <FlowEdge
                                key={`edge-label-${edge.id}`}
                                edge={edge}
                                source={source}
                                target={target}
                                isDimmed={hasHighlight}
                                isBlocked={isEdgeBlocked(edge)}
                                renderPath={false}
                            />
                        );
                    })}
                    {focusedEdges.map((edge) => {
                        const source = nodeMap.get(edge.fromId);
                        const target = nodeMap.get(edge.toId);
                        if (!source || !target) {
                            return null;
                        }
                        return (
                            <FlowEdge
                                key={`edge-path-focus-${edge.id}`}
                                edge={edge}
                                source={source}
                                target={target}
                                isHighlighted={true}
                                isBlocked={isEdgeBlocked(edge)}
                                renderLabel={false}
                            />
                        );
                    })}
                    {implicitHighlightEdges.map((edge) => {
                        const source = nodeMap.get(edge.fromId);
                        const target = nodeMap.get(edge.toId);
                        if (!source || !target) {
                            return null;
                        }
                        return (
                            <FlowEdge
                                key={`edge-path-implicit-focus-${edge.id}`}
                                edge={edge}
                                source={source}
                                target={target}
                                isHighlighted={true}
                                isBlocked={isEdgeBlocked(edge)}
                                renderLabel={false}
                            />
                        );
                    })}
                    {connectedNodes.map((node) => (
                        <FlowNode
                            key={`node-shape-connected-${node.id}`}
                            node={node}
                            highlightState={"connected"}
                            isStanding={node.id === walkCurrentNodeId}
                            renderLabel={false}
                        />
                    ))}
                    {primaryNodes.map((node) => (
                        <FlowNode
                            key={`node-shape-primary-${node.id}`}
                            node={node}
                            highlightState={"primary"}
                            isStanding={node.id === walkCurrentNodeId}
                            renderLabel={false}
                        />
                    ))}
                    {focusedEdges.map((edge) => {
                        const source = nodeMap.get(edge.fromId);
                        const target = nodeMap.get(edge.toId);
                        if (!source || !target) {
                            return null;
                        }
                        return (
                            <FlowEdge
                                key={`edge-label-focus-${edge.id}`}
                                edge={edge}
                                source={source}
                                target={target}
                                isHighlighted={true}
                                isBlocked={isEdgeBlocked(edge)}
                                renderPath={false}
                            />
                        );
                    })}
                    {baseNodes.map((node) => (
                        <FlowNode
                            key={`node-label-${node.id}`}
                            node={node}
                            isDimmed={hasHighlight}
                            isStanding={node.id === walkCurrentNodeId}
                            renderShape={false}
                        />
                    ))}
                    {connectedNodes.map((node) => (
                        <FlowNode
                            key={`node-label-connected-${node.id}`}
                            node={node}
                            highlightState={"connected"}
                            isStanding={node.id === walkCurrentNodeId}
                            renderShape={false}
                        />
                    ))}
                    {primaryNodes.map((node) => (
                        <FlowNode
                            key={`node-label-primary-${node.id}`}
                            node={node}
                            highlightState={"primary"}
                            isStanding={node.id === walkCurrentNodeId}
                            renderShape={false}
                        />
                    ))}
                </g>
            </svg>
        </Box>
    );
};

export default FlowCanvas;
